import { chakra, IconButton, Tooltip } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { SocketLogMessage } from './FriendlyLogger';
import { LucideCopy } from 'lucide-react';
import { formatTimestamp } from './logCommon';

const Message = chakra('div', {
  base: {
    fontFamily: 'acMonospace',
    whiteSpace: 'preserve',
    fontSize: '12px',
    color: 'black',
  },
});

const CopyButton = chakra(IconButton, {
  base: {
    position: 'absolute',
    right: '0px',
    top: '0px',
    display: 'none',
    cursor: 'pointer',
  },
});

const Wrapper = chakra('div', {
  base: {
    padding: '5px',
    fontFamily: 'acBase',
    position: 'relative',
    _notFirst: {
      borderTop: '1px solid {colors.grey.light.2}',
    },

    _hover: {
      '& > button': {
        display: 'block',
      },
    },
  },
  variants: {
    level: {
      info: {
        background: 'linear-gradient(180deg, {colors.halton.blue/20} 0%, transparent min(33%, 50px))',
      },
      warn: {
        background: 'linear-gradient(180deg, {colors.halton.yellow/20} 0%, transparent min(33%, 50px))',
      },
      error: {
        background: 'linear-gradient(180deg, {colors.halton.red/20} 0%, transparent min(33%, 50px))',
      },
      success: {
        background: 'linear-gradient(180deg, {colors.halton.green/20} 0%, transparent min(33%, 50px))',
      },
    },
  },
});

const Info = chakra('div', {
  base: {
    fontSize: '14px',
    marginBottom: '3px',
    fontFamily: 'acMonospace',
  },
  variants: {
    level: {
      info: { color: 'halton.blue' },
      warn: { color: 'halton.yellow' },
      error: { color: 'halton.red' },
      success: { color: 'halton.green' },
    },
  },
});

const Level = chakra('span', {
  base: {
    fontWeight: '600',
    textTransform: 'uppercase',
    borderLeft: '2px solid {colors.grey.light.2}',
    paddingLeft: '5px',
    marginLeft: '5px',
  },
});

interface LogMessageRowProps {
  row: SocketLogMessage;
}

export const LogMessageRow = ({ row }: LogMessageRowProps) => {
  const { t } = useTranslation('translation', { keyPrefix: 'planner.log.level' });
  const { t: tLog } = useTranslation('translation', { keyPrefix: 'planner.log' });

  const copyText = () => {
    navigator.clipboard.writeText(row.message);
  };
  return (
    <Wrapper level={row.level}>
      <Info level={row.level}>
        {formatTimestamp(row.timestamp)}
        <Level>{t(row.level)}</Level>
      </Info>
      <Message>{row.message}</Message>
      <Tooltip.Root positioning={{ placement: 'left' }}>
        <Tooltip.Trigger asChild>
          <CopyButton size="smaller" variant="ghost" onClick={copyText}>
            <LucideCopy />
          </CopyButton>
        </Tooltip.Trigger>
        <Tooltip.Positioner>
          <Tooltip.Content>{tLog('copyMessage')}</Tooltip.Content>
        </Tooltip.Positioner>
      </Tooltip.Root>
    </Wrapper>
  );
};
